import React, { useEffect, useState } from 'react';
import { useBrreg } from '../../context/BrregContext';
import SafeIcon from '../../common/SafeIcon';
import { motion, AnimatePresence } from 'framer-motion';
import {
  naceCodeListMetadata,
  normalizeNaceCode,
  naceSuggestions as codeListNaceSuggestions,
  municipalities as fallbackMunicipalities,
  counties as fallbackCounties,
  organizationForms,
  searchNaceCodes
} from '../../data/brregCodeLists';
import {
  getSsbCodeLists,
  searchNaceCodes as searchSsbNaceCodes
} from '../../services/ssbKlassService';

const MIN_NACE_SEARCH_LENGTH = 2;
const MIN_GEOGRAPHY_SEARCH_LENGTH = 2;
const MAX_NACE_SUGGESTIONS = 20;
const MAX_GEOGRAPHY_SUGGESTIONS = 20;

const isNaceCode = (value) => /^\d{2}\.\d{3}$/.test(normalizeNaceCode(value));

const normalizeNaceSearchQuery = (value) => {
  const query = String(value || '')
    .trim()
    .toLowerCase()
    .replace(/\s/g, '')
    .replace(',', '.');

  if (/^\d{3,5}$/.test(query)) {
    return `${query.slice(0, 2)}.${query.slice(2)}`;
  }

  return query;
};

const rankNaceSuggestions = (suggestions, query) => {
  const rawQuery = String(query || '').trim().toLowerCase();
  const normalizedQuery = normalizeNaceSearchQuery(query);
  const compactQuery = normalizedQuery.replace(/\D/g, '');

  return suggestions
    .map((suggestion, index) => {
      const code = String(suggestion.code || '').trim().toLowerCase();
      const normalizedCode = normalizeNaceSearchQuery(code);
      const compactCode = normalizedCode.replace(/\D/g, '');
      const searchableText = String(
        suggestion.searchableText
        || suggestion.label
        || suggestion.name
        || ''
      ).toLowerCase();

      let rank = 3;

      if (normalizedCode === normalizedQuery || (
        compactQuery && compactCode === compactQuery
      )) {
        rank = 0;
      } else if (
        normalizedCode.startsWith(normalizedQuery)
        || (compactQuery && compactCode.startsWith(compactQuery))
      ) {
        rank = 1;
      } else if (
        searchableText.includes(rawQuery)
        || searchableText.includes(normalizedQuery)
      ) {
        rank = 2;
      }

      return { suggestion, index, rank };
    })
    .sort((left, right) => left.rank - right.rank || left.index - right.index)
    .map(({ suggestion }) => suggestion);
};

const searchGeographyCodes = (items, query, codeLength) => {
  const value = String(query || '').trim().toLowerCase();

  if (!value) return [];

  const compactQuery = value.replace(/\D/g, '');
  const normalizedCodeQuery = compactQuery.padStart(codeLength, '0').slice(-codeLength);

  return items.filter((item) => {
    const searchableText = String(item.searchableText || '').toLowerCase();
    const name = String(item.name || '').toLowerCase();
    const code = String(item.code || '').toLowerCase();
    const compactCode = code.replace(/\D/g, '');

    return searchableText.includes(value)
      || name.includes(value)
      || code.includes(normalizedCodeQuery)
      || (compactQuery && compactCode.includes(compactQuery));
  });
};

const ImportJobBuilder = ({ onJobStarted }) => {
  const { previewImport, runImportJob, settings } = useBrreg();

  const [segmentName, setSegmentName] = useState('');
  const [filters, setFilters] = useState({
    naeringskode: '',
    kommune: '',
    fylke: '',
    orgform: '',
    navn: '',
    fraAntallAnsatte: '',
    tilAntallAnsatte: '',
    fraStiftelsesdato: '',
    tilStiftelsesdato: '',
    registrertIMvaregisteret: '',
    registrertIForetaksregisteret: '',
    sort: '',
    aktiv: true,
    has_website: false,
    max_rows_target: 50
  });

  const [naceInput, setNaceInput] = useState('');
  const [municipalityInput, setMunicipalityInput] = useState('');
  const [countyInput, setCountyInput] = useState('');
  const [selectedNace, setSelectedNace] = useState(null);
  const [selectedCounty, setSelectedCounty] = useState(null);
  const [municipalitySuggestions, setMunicipalitySuggestions] = useState([]);
  const [countySuggestions, setCountySuggestions] = useState([]);
  const [previewCount, setPreviewCount] = useState(null);
  const [loading, setLoading] = useState(false);
  const [codeLists, setCodeLists] = useState({
    naceCodes: [],
    municipalities: [],
    counties: [],
    metadata: null
  });
  const [codeListsLoading, setCodeListsLoading] = useState(true);
  const [codeListsError, setCodeListsError] = useState(null);
  const [isUsingFallbackCodeLists, setIsUsingFallbackCodeLists] = useState(true);
  const [matchingSuggestions, setMatchingSuggestions] = useState([]);

  const activeNaceCodes = codeLists.naceCodes.length > 0
    ? codeLists.naceCodes
    : codeListNaceSuggestions;
  const activeMunicipalities = codeLists.municipalities.length > 0
    ? codeLists.municipalities
    : fallbackMunicipalities;
  const activeCounties = codeLists.counties.length > 0
    ? codeLists.counties
    : fallbackCounties;
  const selectedMunicipality = activeMunicipalities.find(
    (municipality) => municipality.code === filters.kommune
  );
  const naceMetadata = codeLists.metadata?.nace || naceCodeListMetadata;
  const municipalityMetadata = codeLists.metadata?.municipalities;
  const countyMetadata = codeLists.metadata?.counties;
  const ssbListsAvailable = Boolean(
    naceMetadata?.source === 'SSB_KLASS'
    && municipalityMetadata?.source === 'SSB_KLASS'
    && countyMetadata?.source === 'SSB_KLASS'
  );

  useEffect(() => {
    let mounted = true;

    async function loadCodeLists() {
      setCodeListsLoading(true);
      setCodeListsError(null);

      try {
        const result = await getSsbCodeLists();

        if (!mounted) return;

        const usingFallback = [
          result?.metadata?.nace,
          result?.metadata?.municipalities,
          result?.metadata?.counties
        ].some((metadata) => metadata?.source === 'LOCAL_FALLBACK');

        if (import.meta.env.DEV) {
          console.info('[BRREG kodeverk]', {
            naceCount: result?.naceCodes?.length || 0,
            municipalityCount: result?.municipalities?.length || 0,
            countyCount: result?.counties?.length || 0,
            naceSource: result?.metadata?.nace?.source,
            municipalitySource: result?.metadata?.municipalities?.source,
            countySource: result?.metadata?.counties?.source,
            importedAt: result?.metadata?.nace?.importedAt
              || result?.metadata?.municipalities?.importedAt
              || result?.metadata?.counties?.importedAt,
            usingFallback
          });
        }

        setCodeLists(result);
        setIsUsingFallbackCodeLists(Boolean(usingFallback));
      } catch (error) {
        if (!mounted) return;

        console.error('Kunne ikke laste SSB kodeverk:', error);
        setCodeListsError('Kunne ikke laste offisielle kodeverk. Lokal fallback brukes.');
        setIsUsingFallbackCodeLists(true);
      } finally {
        if (mounted) {
          setCodeListsLoading(false);
        }
      }
    }

    loadCodeLists();

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    let mounted = true;

    async function loadSuggestions() {
      if (codeListsLoading) {
        setMatchingSuggestions([]);
        return;
      }

      if (!naceInput.trim() || naceInput.trim().length < MIN_NACE_SEARCH_LENGTH) {
        setMatchingSuggestions([]);
        return;
      }

      if (codeLists.naceCodes.length === 0) {
        const fallbackSuggestions = rankNaceSuggestions(
          searchNaceCodes(naceInput),
          naceInput
        );

        if (mounted) {
          setMatchingSuggestions(fallbackSuggestions.slice(0, MAX_NACE_SUGGESTIONS));
        }

        return;
      }

      try {
        const suggestions = await searchSsbNaceCodes(naceInput);

        if (mounted) {
          const rankedSuggestions = rankNaceSuggestions(suggestions, naceInput);
          setMatchingSuggestions(rankedSuggestions.slice(0, MAX_NACE_SUGGESTIONS));
        }
      } catch (error) {
        if (mounted) {
          const fallbackSuggestions = rankNaceSuggestions(
            searchNaceCodes(naceInput),
            naceInput
          );
          setMatchingSuggestions(fallbackSuggestions.slice(0, MAX_NACE_SUGGESTIONS));
        }
      }
    }

    loadSuggestions();

    return () => {
      mounted = false;
    };
  }, [naceInput, codeLists.naceCodes, activeNaceCodes, codeListsLoading]);

  useEffect(() => {
    if (
      codeListsLoading
      || municipalityInput.trim().length < MIN_GEOGRAPHY_SEARCH_LENGTH
    ) {
      setMunicipalitySuggestions([]);
      return;
    }

    const suggestions = searchGeographyCodes(
      activeMunicipalities,
      municipalityInput,
      4
    );

    setMunicipalitySuggestions(suggestions.slice(0, MAX_GEOGRAPHY_SUGGESTIONS));
  }, [municipalityInput, activeMunicipalities, codeListsLoading]);

  useEffect(() => {
    if (
      codeListsLoading
      || countyInput.trim().length < MIN_GEOGRAPHY_SEARCH_LENGTH
    ) {
      setCountySuggestions([]);
      return;
    }

    const suggestions = searchGeographyCodes(
      activeCounties,
      countyInput,
      2
    );

    setCountySuggestions(suggestions.slice(0, MAX_GEOGRAPHY_SUGGESTIONS));
  }, [countyInput, activeCounties, codeListsLoading]);

  const handleRefreshCodeLists = async () => {
    setCodeListsLoading(true);
    setCodeListsError(null);
    setMatchingSuggestions([]);
    setMunicipalitySuggestions([]);
    setCountySuggestions([]);

    try {
      const result = await getSsbCodeLists({ forceRefresh: true });

      const usingFallback = [
        result?.metadata?.nace,
        result?.metadata?.municipalities,
        result?.metadata?.counties
      ].some((metadata) => metadata?.source === 'LOCAL_FALLBACK');

      if (import.meta.env.DEV) {
        console.info('[BRREG kodeverk]', {
          naceCount: result?.naceCodes?.length || 0,
          municipalityCount: result?.municipalities?.length || 0,
          countyCount: result?.counties?.length || 0,
          naceSource: result?.metadata?.nace?.source,
          municipalitySource: result?.metadata?.municipalities?.source,
          countySource: result?.metadata?.counties?.source,
          importedAt: result?.metadata?.nace?.importedAt
            || result?.metadata?.municipalities?.importedAt
            || result?.metadata?.counties?.importedAt,
          usingFallback
        });
      }

      setCodeLists(result);
      setIsUsingFallbackCodeLists(Boolean(usingFallback));
    } catch (error) {
      console.error('Kunne ikke oppdatere SSB kodeverk:', error);
      setCodeListsError('Kunne ikke oppdatere fra SSB. Eksisterende cache eller fallback brukes.');
      setIsUsingFallbackCodeLists(true);
    } finally {
      setCodeListsLoading(false);
    }
  };

  const validateNaceInput = () => {
    const value = normalizeNaceCode(naceInput);

    if (!value || isNaceCode(value)) return true;

    alert('Velg en næringskode fra forslagene, eller la feltet stå tomt.');
    return false;
  };

  const validateFilters = () => {
    const fromEmployees = filters.fraAntallAnsatte === ''
      ? null
      : Number(filters.fraAntallAnsatte);
    const toEmployees = filters.tilAntallAnsatte === ''
      ? null
      : Number(filters.tilAntallAnsatte);

    if (fromEmployees !== null && (!Number.isInteger(fromEmployees) || fromEmployees < 0)) {
      alert('Ansatte fra må være et helt tall på 0, 1 eller 5 eller høyere.');
      return false;
    }

    if (toEmployees !== null && (!Number.isInteger(toEmployees) || toEmployees < 0)) {
      alert('Ansatte til må være et helt tall på 0, 4 eller 5 eller høyere.');
      return false;
    }

    if (fromEmployees !== null && fromEmployees >= 2 && fromEmployees <= 4) {
      alert('BRREG støtter ikke fra-verdi 2–4. Bruk 1, 5 eller høyere.');
      return false;
    }

    if (toEmployees !== null && toEmployees >= 1 && toEmployees <= 3) {
      alert('BRREG støtter ikke til-verdi 1–3. Bruk 0, 4 eller 5 eller høyere.');
      return false;
    }

    if (fromEmployees !== null && toEmployees !== null && fromEmployees > toEmployees) {
      alert('Ansatte fra kan ikke være høyere enn ansatte til.');
      return false;
    }

    if (
      filters.fraStiftelsesdato
      && filters.tilStiftelsesdato
      && filters.fraStiftelsesdato > filters.tilStiftelsesdato
    ) {
      alert('Stiftet fra kan ikke være etter stiftet til.');
      return false;
    }

    return true;
  };

  const validateBeforeRequest = () => validateNaceInput() && validateFilters();

  const handleNaceChange = (value) => {
    const normalizedValue = normalizeNaceCode(value);

    setNaceInput(value);
    setSelectedNace(null);
    setFilters((currentFilters) => ({
      ...currentFilters,
      naeringskode: isNaceCode(normalizedValue) ? normalizedValue : ''
    }));
  };

  const handleNaceSelect = (suggestion) => {
    setNaceInput('');
    setMatchingSuggestions([]);
    setSelectedNace(suggestion);
    setFilters((currentFilters) => ({
      ...currentFilters,
      naeringskode: suggestion.code
    }));
  };

  const handleMunicipalityChange = (value) => {
    setMunicipalityInput(value);
    setFilters((currentFilters) => ({
      ...currentFilters,
      kommune: ''
    }));
  };

  const handleMunicipalitySelect = (municipality) => {
    setMunicipalityInput('');
    setMunicipalitySuggestions([]);
    setFilters((currentFilters) => ({
      ...currentFilters,
      kommune: municipality.code
    }));
  };

  const handleRemoveMunicipality = () => {
    setMunicipalityInput('');
    setMunicipalitySuggestions([]);
    setFilters((currentFilters) => ({
      ...currentFilters,
      kommune: ''
    }));
  };

  const handleCountyChange = (value) => {
    setCountyInput(value);
    setSelectedCounty(null);
    setFilters((currentFilters) => ({
      ...currentFilters,
      fylke: ''
    }));
  };

  const handleCountySelect = (county) => {
    setCountyInput('');
    setCountySuggestions([]);
    setSelectedCounty(county);
    setFilters((currentFilters) => ({
      ...currentFilters,
      fylke: county.code
    }));
  };

  const handleRemoveCounty = () => {
    setCountyInput('');
    setCountySuggestions([]);
    setSelectedCounty(null);
    setFilters((currentFilters) => ({
      ...currentFilters,
      fylke: ''
    }));
  };

  const handlePreview = async () => {
    if (!validateBeforeRequest()) return;

    setLoading(true);
    try {
      const count = await previewImport(filters);
      setPreviewCount(count);
    } catch (e) {
      alert("Feil ved kontakt med BRREG API. Vennligst prøv igjen.");
    } finally {
      setLoading(false);
    }
  };

  const handleRun = async () => {
    if (!segmentName) return alert("Vennligst gi importen et navn først.");
    if (!validateBeforeRequest()) return;

    setLoading(true);
    
    // Kjør importen og få tilbake Job ID
    const jobId = await runImportJob(segmentName, filters);
    
    setLoading(false);
    
    // Hvis importen var vellykket, send Job ID tilbake for å bytte fane
    if (jobId) {
      onJobStarted(jobId);
    }
  };

  const updateFilter = (field, value) => {
    setFilters((currentFilters) => ({
      ...currentFilters,
      [field]: value
    }));
  };

  return (
    <div className="bg-white p-8 rounded-[2rem] border border-slate-200 shadow-sm relative overflow-hidden">
      {/* Decorative background element */}
      <div className="absolute top-0 right-0 w-64 h-64 bg-blue-50 rounded-bl-full -mr-16 -mt-16 z-0 pointer-events-none"></div>
      
      <div className="relative z-10 flex justify-between items-start mb-8 border-b border-slate-100 pb-6">
        <div>
          <h2 className="text-2xl font-black text-slate-900 tracking-tight">Ny Importjobb</h2>
          <p className="text-slate-500 text-sm font-medium mt-1">Definer målgruppe direkte fra Enhetsregisteret</p>
        </div>
        <div className="text-right bg-slate-50 px-4 py-2 rounded-xl border border-slate-100">
          <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Din Kvote</p>
          <p className="text-sm font-bold text-slate-700">{settings?.quota_rows_used || 0} / 25000 rader</p>
        </div>
      </div>

      <div className="relative z-10 grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <div className="md:col-span-2">
          <label className="block text-xs font-black text-slate-500 uppercase tracking-widest mb-2">Segment Navn <span className="text-red-500">*</span></label>
          <input 
            className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none font-bold text-base transition-all" 
            placeholder="F.eks. Teknologiselskaper i Oslo" 
            value={segmentName} 
            onChange={(e) => setSegmentName(e.target.value)} 
          />
        </div>
        
        <div>
          <label className="block text-xs font-black text-slate-500 uppercase tracking-widest mb-2">Næringskode / bransje</label>
          <input 
            className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none text-sm font-medium transition-all" 
            placeholder="F.eks. tømrer, bygg, restaurant eller 43.320"
            value={naceInput} 
            onChange={(e) => handleNaceChange(e.target.value)} 
          />
          <p className="mt-2 text-xs text-slate-400">
            Skriv bransjeord eller NACE-kode. La feltet stå tomt for bred import.
          </p>

          {codeListsLoading && (
            <p className="mt-2 text-xs font-medium text-slate-400">
              Laster offisielle kodeverk fra SSB …
            </p>
          )}

          {naceInput.trim().length > 0
            && naceInput.trim().length < MIN_NACE_SEARCH_LENGTH
            && !codeListsLoading && (
              <p className="mt-2 text-xs font-medium text-slate-400">
                Skriv minst {MIN_NACE_SEARCH_LENGTH} tegn for å vise forslag.
              </p>
            )}

          {matchingSuggestions.length > 0 && (
            <div className="mt-2 max-h-72 overflow-y-auto rounded-xl border border-slate-200 bg-white shadow-lg">
              <p className="px-4 py-2 text-[10px] font-black uppercase tracking-widest text-slate-400 border-b border-slate-100">
                {ssbListsAvailable
                  ? `Offisielt kodeverk fra SSB (${naceMetadata.count || activeNaceCodes.length} koder)`
                  : `Forslag – ikke komplett register (${naceCodeListMetadata.standard})`}
              </p>
              {matchingSuggestions.map((suggestion) => (
                <button
                  key={suggestion.code}
                  type="button"
                  onClick={() => handleNaceSelect(suggestion)}
                  className="w-full px-4 py-3 text-left text-sm font-bold text-slate-700 hover:bg-blue-50 hover:text-blue-700 transition-colors"
                >
                  {suggestion.code} — {suggestion.label}
                </button>
              ))}
            </div>
          )}

          {selectedNace && (
            <p className="mt-2 text-xs font-bold text-blue-700">
              Valgt: {selectedNace.code} — {selectedNace.label}
            </p>
          )}

          {naceInput.trim().length >= MIN_NACE_SEARCH_LENGTH
            && matchingSuggestions.length === 0
            && !codeListsLoading && (
            <p className="mt-2 text-xs font-medium text-amber-600">
              Ingen treff.
            </p>
          )}
        </div>
        
        <div>
          <label className="block text-xs font-black text-slate-500 uppercase tracking-widest mb-2">Kommune (Nr)</label>
          <input
            className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none text-sm font-medium transition-all"
            placeholder="F.eks. 0301 (Oslo)"
            value={municipalityInput}
            onChange={(e) => handleMunicipalityChange(e.target.value)}
          />
          <p className="mt-2 text-xs text-slate-400">
            Søk på kommunenavn eller kommunenummer. Skriv minst {MIN_GEOGRAPHY_SEARCH_LENGTH} tegn.
          </p>

          {municipalitySuggestions.length > 0 && (
            <div className="mt-2 max-h-72 overflow-y-auto rounded-xl border border-slate-200 bg-white shadow-lg">
              {municipalitySuggestions.map((municipality) => (
                <button
                  key={municipality.code}
                  type="button"
                  onClick={() => handleMunicipalitySelect(municipality)}
                  className="w-full px-4 py-3 text-left text-sm font-bold text-slate-700 hover:bg-blue-50 hover:text-blue-700 transition-colors"
                >
                  {municipality.name} ({municipality.code})
                </button>
              ))}
            </div>
          )}

          {municipalityInput.trim().length >= MIN_GEOGRAPHY_SEARCH_LENGTH
            && municipalitySuggestions.length === 0
            && !codeListsLoading && (
            <p className="mt-2 text-xs font-medium text-amber-600">
              Ingen treff.
            </p>
          )}

          <p className="mt-2 text-xs text-amber-600">
            {ssbListsAvailable
              ? `Offisielt kommune-/fylkeskodeverk fra SSB (${municipalityMetadata.count || activeMunicipalities.length} kommuner)`
              : 'Delvis kommuneliste – skal erstattes med komplett offisiell liste'}
          </p>
          {selectedMunicipality && (
            <div className="mt-1 inline-flex items-center gap-2 rounded-lg border border-blue-100 bg-blue-50 px-3 py-1.5 text-xs font-bold text-blue-700">
              <span>{selectedMunicipality.name} ({selectedMunicipality.code})</span>
              <button
                type="button"
                onClick={handleRemoveMunicipality}
                className="text-blue-500 hover:text-blue-800"
                aria-label="Fjern valgt kommune"
              >
                ×
              </button>
            </div>
          )}
        </div>

        <div>
          <label className="block text-xs font-black text-slate-500 uppercase tracking-widest mb-2">Fylke</label>
          <input
            className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none text-sm font-medium transition-all"
            placeholder="Søk på fylkesnavn eller fylkesnummer"
            value={countyInput}
            onChange={(e) => handleCountyChange(e.target.value)}
          />
          <p className="mt-2 text-xs text-slate-400">
            Søk på fylkesnavn eller fylkesnummer. Skriv minst {MIN_GEOGRAPHY_SEARCH_LENGTH} tegn.
          </p>

          {countySuggestions.length > 0 && (
            <div className="mt-2 max-h-72 overflow-y-auto rounded-xl border border-slate-200 bg-white shadow-lg">
              {countySuggestions.map((county) => (
                <button
                  key={county.code}
                  type="button"
                  onClick={() => handleCountySelect(county)}
                  className="w-full px-4 py-3 text-left text-sm font-bold text-slate-700 hover:bg-blue-50 hover:text-blue-700 transition-colors"
                >
                  {county.name} ({county.code})
                </button>
              ))}
            </div>
          )}

          {countyInput.trim().length >= MIN_GEOGRAPHY_SEARCH_LENGTH
            && countySuggestions.length === 0
            && !codeListsLoading && (
            <p className="mt-2 text-xs font-medium text-amber-600">
              Ingen treff.
            </p>
          )}

          {selectedCounty && (
            <div className="mt-1 inline-flex items-center gap-2 rounded-lg border border-blue-100 bg-blue-50 px-3 py-1.5 text-xs font-bold text-blue-700">
              <span>{selectedCounty.name} ({selectedCounty.code})</span>
              <button
                type="button"
                onClick={handleRemoveCounty}
                className="text-blue-500 hover:text-blue-800"
                aria-label="Fjern valgt fylke"
              >
                ×
              </button>
            </div>
          )}
        </div>
        
        <div>
          <label className="block text-xs font-black text-slate-500 uppercase tracking-widest mb-2">Organisasjonsform</label>
          <select 
            className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none text-sm font-medium transition-all appearance-none cursor-pointer" 
            value={filters.orgform} 
            onChange={(e) => setFilters({ ...filters, orgform: e.target.value })}
          >
            <option value="">Alle former</option>
            {organizationForms.map((form) => (
              <option key={form.code} value={form.code}>{form.display}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-xs font-black text-slate-500 uppercase tracking-widest mb-2">Antall Leads (Maks 100)</label>
          <div className="flex items-center gap-4">
            <input 
              type="range" 
              min="10" 
              max="100" 
              step="10"
              value={filters.max_rows_target} 
              onChange={(e) => setFilters({ ...filters, max_rows_target: parseInt(e.target.value) })}
              className="flex-1 h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
            />
            <span className="w-12 text-center font-black text-slate-700 bg-slate-100 py-1.5 rounded-lg text-sm border border-slate-200">
              {filters.max_rows_target}
            </span>
          </div>
        </div>

        <div className="md:col-span-2 border-t border-slate-100 pt-6">
          <h3 className="text-sm font-black text-slate-800 uppercase tracking-widest mb-5">Flere filtre</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="md:col-span-2">
              <label className="block text-xs font-black text-slate-500 uppercase tracking-widest mb-2">Firmanavn inneholder</label>
              <input
                className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none text-sm font-medium transition-all"
                placeholder="F.eks. bygg, tømrer, consulting"
                value={filters.navn}
                onChange={(e) => updateFilter('navn', e.target.value)}
              />
              <p className="mt-2 text-xs text-slate-400">
                Søk i selskapsnavn. Kan kombineres med bransje og kommune.
              </p>
            </div>

            <div>
              <label className="block text-xs font-black text-slate-500 uppercase tracking-widest mb-2">Ansatte fra</label>
              <input
                type="number"
                min="0"
                className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none text-sm font-medium transition-all"
                placeholder="F.eks. 5"
                value={filters.fraAntallAnsatte}
                onChange={(e) => updateFilter('fraAntallAnsatte', e.target.value)}
              />
            </div>

            <div>
              <label className="block text-xs font-black text-slate-500 uppercase tracking-widest mb-2">Ansatte til</label>
              <input
                type="number"
                min="0"
                className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none text-sm font-medium transition-all"
                placeholder="F.eks. 50"
                value={filters.tilAntallAnsatte}
                onChange={(e) => updateFilter('tilAntallAnsatte', e.target.value)}
              />
            </div>

            <p className="md:col-span-2 -mt-2 text-xs text-slate-400">
              La stå tomt hvis antall ansatte ikke skal brukes.
            </p>

            <div>
              <label className="block text-xs font-black text-slate-500 uppercase tracking-widest mb-2">Stiftet fra</label>
              <input
                type="date"
                className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none font-medium text-sm transition-all"
                value={filters.fraStiftelsesdato}
                onChange={(e) => setFilters({ ...filters, fraStiftelsesdato: e.target.value })}
              />
            </div>

            <div>
              <label className="block text-xs font-black text-slate-500 uppercase tracking-widest mb-2">Stiftet til</label>
              <input
                type="date"
                className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none font-medium text-sm transition-all"
                value={filters.tilStiftelsesdato}
                onChange={(e) => setFilters({ ...filters, tilStiftelsesdato: e.target.value })}
              />
            </div>

            <div>
              <label className="block text-xs font-black text-slate-500 uppercase tracking-widest mb-2">MVA-registrert</label>
              <select
                className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none text-sm font-medium transition-all appearance-none cursor-pointer"
                value={filters.registrertIMvaregisteret}
                onChange={(e) => updateFilter('registrertIMvaregisteret', e.target.value)}
              >
                <option value="">Alle</option>
                <option value="true">Ja</option>
                <option value="false">Nei</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-black text-slate-500 uppercase tracking-widest mb-2">Foretaksregisteret</label>
              <select
                className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none text-sm font-medium transition-all appearance-none cursor-pointer"
                value={filters.registrertIForetaksregisteret}
                onChange={(e) => updateFilter('registrertIForetaksregisteret', e.target.value)}
              >
                <option value="">Alle</option>
                <option value="true">Ja</option>
                <option value="false">Nei</option>
              </select>
            </div>

            <div className="md:col-span-2">
              <label className="block text-xs font-black text-slate-500 uppercase tracking-widest mb-2">Sortering</label>
              <select
                className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none text-sm font-medium transition-all appearance-none cursor-pointer"
                value={filters.sort}
                onChange={(e) => setFilters({ ...filters, sort: e.target.value })}
              >
                <option value="">Relevans / standard</option>
                <option value="navn,ASC">Navn A–Å</option>
                <option value="antallAnsatte,ASC">Antall ansatte stigende</option>
                <option value="antallAnsatte,DESC">Antall ansatte synkende</option>
                <option value="stiftelsesdato,DESC">Stiftelsesdato nyeste først</option>
                <option value="stiftelsesdato,ASC">Stiftelsesdato eldste først</option>
                <option value="registreringsdatoEnhetsregisteret,DESC">Registreringsdato nyeste først</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      <div className="relative z-10 flex flex-wrap gap-4 mb-8 p-5 bg-slate-50 rounded-2xl border border-slate-100">
        <label className="flex items-start gap-3 cursor-pointer group">
          <input 
            type="checkbox" 
            checked={filters.has_website} 
            onChange={(e) => setFilters({ ...filters, has_website: e.target.checked })} 
            className="w-5 h-5 mt-0.5 rounded border-slate-300 text-blue-600 focus:ring-blue-500 transition-all" 
          />
          <span className="flex flex-col gap-1">
            <span className="text-sm font-bold text-slate-700 group-hover:text-slate-900 transition-colors">Krever registrert nettside</span>
            <span className="text-xs font-medium text-slate-400">Brukes som krav i vask/godkjenning. Noen BRREG-treff kan mangle nettside.</span>
          </span>
        </label>
        
        <label className="flex items-start gap-3 cursor-pointer group">
          <input 
            type="checkbox" 
            checked={filters.aktiv} 
            onChange={(e) => setFilters({ ...filters, aktiv: e.target.checked })} 
            className="w-5 h-5 mt-0.5 rounded border-slate-300 text-blue-600 focus:ring-blue-500 transition-all" 
          />
          <span className="flex flex-col gap-1">
            <span className="text-sm font-bold text-slate-700 group-hover:text-slate-900 transition-colors">Kun aktive selskap (Ikke konkurs)</span>
            <span className="text-xs font-medium text-slate-400">Brukes for å filtrere bort selskaper som ikke skal bearbeides.</span>
          </span>
        </label>
      </div>

      <div className="relative z-10 mb-8 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-slate-100 bg-slate-50 p-4">
        <div className="text-xs text-slate-500">
          <p className="font-black text-slate-700">Kodeverkstatus</p>
          {codeListsLoading && (
            <p className="mt-1 font-medium text-slate-400">
              Laster offisielle kodeverk fra SSB …
            </p>
          )}
          {isUsingFallbackCodeLists ? (
            <p className="font-medium text-amber-600">
              Lokal fallback brukes dersom SSB-data ikke er tilgjengelig.
            </p>
          ) : (
            <p className="font-medium text-emerald-700">
              Offisielt kodeverk fra SSB brukes som primærkilde.
            </p>
          )}
          {!codeListsLoading && (
            <div className="mt-1 space-y-0.5">
              <p>
                NACE: {naceMetadata?.source || 'UKJENT'} – {codeLists.naceCodes.length || activeNaceCodes.length} koder
              </p>
              <p>
                Kommuner: {municipalityMetadata?.source || 'UKJENT'} – {codeLists.municipalities.length || activeMunicipalities.length}
              </p>
              <p>
                Fylker: {countyMetadata?.source || 'UKJENT'} – {codeLists.counties.length}
              </p>
              <p>
                Sist oppdatert: {naceMetadata?.importedAt
                  ? new Date(naceMetadata.importedAt).toLocaleString('no-NO')
                  : municipalityMetadata?.importedAt
                    ? new Date(municipalityMetadata.importedAt).toLocaleString('no-NO')
                    : countyMetadata?.importedAt
                      ? new Date(countyMetadata.importedAt).toLocaleString('no-NO')
                      : 'Ikke tilgjengelig'}
              </p>
              <p>Fallback: {isUsingFallbackCodeLists ? 'Ja' : 'Nei'}</p>
            </div>
          )}
          {codeListsError && (
            <p className="mt-1 text-amber-600">{codeListsError}</p>
          )}
          {!isUsingFallbackCodeLists && (
            <p className="mt-1">
              NACE: {naceMetadata.count || activeNaceCodes.length}
              {' · '}
              Kommuner: {municipalityMetadata.count || activeMunicipalities.length}
              {' · '}
              Fylker: {countyMetadata.count || codeLists.counties.length}
              {naceMetadata.importedAt && (
                <> · Sist oppdatert: {new Date(naceMetadata.importedAt).toLocaleString('no-NO')}</>
              )}
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={handleRefreshCodeLists}
          disabled={codeListsLoading}
          className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-black uppercase tracking-wider text-slate-600 transition-colors hover:border-blue-300 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {codeListsLoading ? 'Oppdaterer...' : 'Oppdater fra SSB'}
        </button>
      </div>

      <div className="relative z-10 flex flex-col sm:flex-row items-center justify-between gap-6 pt-6 border-t border-slate-100">
        <div className="flex items-center gap-4 w-full sm:w-auto">
          <button 
            onClick={handlePreview} 
            disabled={loading} 
            className="w-full sm:w-auto px-6 py-3 bg-white border-2 border-slate-200 text-slate-700 rounded-xl font-black text-xs uppercase tracking-widest hover:bg-slate-50 hover:border-slate-300 transition-all focus:ring-4 focus:ring-slate-100 outline-none disabled:opacity-50"
          >
            {loading && !segmentName ? 'Sjekker...' : 'Forhåndsvis antall'}
          </button>
          
          <AnimatePresence>
            {previewCount !== null && (
              <motion.div 
                initial={{ opacity: 0, x: -10 }} 
                animate={{ opacity: 1, x: 0 }} 
                className="flex items-center gap-2 bg-emerald-50 text-emerald-700 px-4 py-2 rounded-xl border border-emerald-100"
              >
                <SafeIcon icon="✅" className="w-4 h-4" />
                <span className="font-black text-sm">{previewCount.toLocaleString('no-NO')} treff</span>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <button 
          onClick={handleRun} 
          disabled={loading || !segmentName} 
          className="w-full sm:w-auto px-8 py-4 bg-blue-600 text-white rounded-xl font-black text-xs uppercase tracking-[0.2em] hover:bg-blue-700 shadow-xl shadow-blue-200 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3 active:scale-95"
        >
          {loading && segmentName ? (
            <>
              <SafeIcon icon="🔄" className="animate-spin w-4 h-4" />
              Importerer...
            </>
          ) : (
            <>
              <SafeIcon icon="📥" className="w-4 h-4" />
              Kjør Import
            </>
          )}
        </button>
      </div>
      
      <p className="relative z-10 mt-6 text-[10px] text-slate-400 text-center font-medium">
        <SafeIcon icon="ℹ️" className="inline mr-1" />
        {settings?.license_notice}
      </p>
    </div>
  );
};

export default ImportJobBuilder;